<script setup lang="ts">
import { computed, defineAsyncComponent, provide, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { boardHistoryTimespanChanged } from "@/analytics/events/history";
import { trackEvent } from "@/analytics/track";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import DropdownTrigger from "@/components/ui/DropdownMenu/DropdownTrigger.vue";
import { menuListConfigKey } from "@/components/ui/MenuList/injectKeys";
import SelectableList from "@/components/ui/SelectableList/SelectableList.vue";
import { MINUTES } from "@/model/Settings";
import { useActivityStore } from "@/store/activity";
import type { DateFilter, DateItem } from "@/store/helpers/activityDateFilters";
import { isNow } from "@/utils/date";
import { toShortDateString, toTimeString } from "@/utils/dateFormat";
import type { UnknownRecord } from "@/utils/type";

const DatePicker = defineAsyncComponent(() => import("./DatePicker.vue"));

const { t } = useI18n();

const model = defineModel<DateItem | undefined>();

const props = defineProps<{ values: DateFilter[]; range: boolean }>();

const dropdownMenuRef = useTemplateRef("dropdownMenu");

provide(menuListConfigKey, { selectable: true });

const customDate = ref<[Date, Date] | null>(null);
const customDateItem = computed<DateFilter | null>(() =>
  customDate.value
    ? {
        id: "custom" as DateFilter["id"],
        name: [/*$t*/ "boardDiff.dateCustomRange", /*$t*/ "boardDiff.dateCustom"],
        fromDate: () => customDate.value?.[0] || new Date(),
        toDate: () => customDate.value?.[1] || new Date(),
        show: () => false,
      }
    : null,
);

const selection = computed(
  () =>
    (model.value?.id === "custom" && customDateItem.value) ||
    props.values.find((value) => value.id === model.value?.id) ||
    props.values[0],
);

setModel(selection.value);

function select(value: DateFilter) {
  trackEvent(boardHistoryTimespanChanged(value.id, useActivityStore().tab));
  setModel(value);
}

function setModel(value: DateFilter) {
  model.value = { id: value.id, fromDate: value.fromDate(), toDate: value.toDate() };
}

function dateRange(value: Pick<DateFilter, "fromDate" | "toDate">) {
  return {
    from: toShortDateString(value.fromDate()),
    to: isNow(value.toDate(), 5 * MINUTES) // show 'now' if the datetime is now±5 min
      ? t("boardDiff.now")
      : toShortDateString(value.toDate()),
  };
}

/**
 * Generate the dropdown label, in the format:
 * `${selection.name} (${date.from} ~-${date.to})`
 */
const triggerLabel = computed(() => {
  const fromDate = selection.value.fromDate();
  const toDate = selection.value.toDate();

  // Special case: if the dates are the same, show the times
  if (fromDate.toDateString() === toDate.toDateString()) {
    const args = {
      date: toShortDateString(fromDate),
      from: toTimeString(fromDate),
      to: toTimeString(toDate),
    };
    return dateName(selection.value, args);
  }

  return dateName(selection.value, dateRange(selection.value));
});

function dateName(date: DateFilter, args: UnknownRecord = {}) {
  return props.range ? t(date.name[0], args) : t(date.name[1], args);
}

function dateValues(date: DateFilter) {
  return props.range ? t("boardDiff.dates", dateRange(date)) : t("boardDiff.date", dateRange(date));
}

/**
 * When the user finalizes their selection in the date picker, update the model
 */
function onDatesSelected() {
  if (customDate.value?.[0] && customDate.value?.[1] && customDateItem.value) {
    model.value = { id: "custom", fromDate: customDate.value[0], toDate: customDate.value[1] };
    dropdownMenuRef.value?.close();
  }
}
</script>

<template>
  <div class="date-filter">
    <DropdownMenu ref="dropdownMenu" width="lg" class="date-filter-dropdown">
      <template #trigger="{ isOpen }">
        <DropdownTrigger
          :value="triggerLabel"
          :aria-expanded="isOpen"
          aria-haspopup="listbox"
          aria-controls="date-filter-list"
          :icon-after="null"
          icon="general/calendar"
        />
      </template>
      <SelectableList
        id="date-filter-list"
        v-keyboard-nav
        :header="$t('boardDiff.dateRange')"
        :model-value="selection"
        :values
        @update:model-value="select"
      >
        <template #default="{ item }">
          <span>{{ dateName(item) }}</span>
        </template>
        <template #after="{ item }">
          <span class="dates">{{ dateValues(item) }}</span>
        </template>
      </SelectableList>
      <DatePicker
        v-model="customDate"
        :selected="selection?.id === 'custom'"
        :range
        @update:model-value="onDatesSelected"
      />
    </DropdownMenu>
  </div>
</template>

<style lang="scss">
.date-filter {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5em;

  .dropdown-menu {
    width: 100%;

    .dropdown-trigger {
      width: 100%;
    }
  }
}

.date-filter-dropdown {
  overflow: visible !important;
}
</style>
